import { useLayoutEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'

const GAP = 4
const VIEWPORT_MARGIN = 8

// A chosen side is kept until a scroll or resize leaves it with less usable room than this (8rem).
export const MIN_USABLE_ROOM = 128

const roomBelow = (anchor, viewport) => viewport.height - anchor.bottom - GAP - VIEWPORT_MARGIN
const roomAbove = anchor => anchor.top - GAP - VIEWPORT_MARGIN
const roomOn = (side, anchor, viewport) => side === 'below' ? roomBelow(anchor, viewport) : roomAbove(anchor)

// Which side of the anchor a box of `height` goes on: below if it fits there, else above if it
// fits there, else whichever side has more room.
export const chooseSide = (anchor, height, viewport) => {
  const below = roomBelow(anchor, viewport)
  const above = roomAbove(anchor)
  if (height <= below) return 'below'
  if (height <= above) return 'above'
  return below >= above ? 'below' : 'above'
}

// Places a box of `size` on the given side of the anchor, clamped horizontally into the viewport.
// It never changes side: a box taller than the room on its side gets a capped height instead, so
// it can never overlap the anchor. A box below the anchor is positioned by `top`; one above it by
// CSS `bottom`, so it grows upward from a fixed edge with no JS needed (no overlapping frame).
export const placeOnSide = (side, anchor, size, viewport) => {
  const left = Math.max(VIEWPORT_MARGIN, Math.min(anchor.left, viewport.width - size.width - VIEWPORT_MARGIN))
  const room = Math.max(0, roomOn(side, anchor, viewport))
  const fits = size.height <= room
  const maxHeight = fits ? undefined : room
  if (side === 'below') return { side, left, top: anchor.bottom + GAP, maxHeight }
  return { side, left, bottom: viewport.height - anchor.top + GAP, maxHeight }
}

// A placement as an inline style, with only the edges that are set.
export const placementStyle = placement => {
  const { left, top, bottom, maxHeight } = placement
  return { left, ...(top !== undefined && { top }), ...(bottom !== undefined && { bottom }), ...(maxHeight !== undefined && { maxHeight }) }
}

const SCROLLING_OVERFLOW = /auto|scroll|overlay/

// True when the anchor lies entirely outside the visible rect of any scrolling ancestor.
const scrolledOutOfView = (anchorElement, anchor) => {
  for (let element = anchorElement.parentElement; element && element !== document.body && element !== document.documentElement; element = element.parentElement) {
    const { overflowX, overflowY } = window.getComputedStyle(element)
    if (!SCROLLING_OVERFLOW.test(`${overflowX} ${overflowY}`)) continue
    const visible = element.getBoundingClientRect()
    // A zero-sized rect means nothing is laid out (e.g. a test DOM), not that the anchor is hidden.
    if (visible.width === 0 || visible.height === 0 || anchor.width === 0 || anchor.height === 0) continue
    if (anchor.bottom <= visible.top || anchor.top >= visible.bottom || anchor.right <= visible.left || anchor.left >= visible.right) return true
  }
  return false
}

// Pure one-shot placement: choose the side from the box's own height, then place it.
export const computeAnchoredPosition = (anchor, size, viewport) =>
  placeOnSide(chooseSide(anchor, size.height, viewport), anchor, size, viewport)

const samePlacement = (a, b) => a && b && a.side === b.side && a.left === b.left && a.top === b.top && a.bottom === b.bottom && a.maxHeight === b.maxHeight

// Positions `popoverRef` against `anchorRef` while `open`.
// - The side is chosen once when it opens, from `capRatio` x viewport height (the box's CSS max
//   height) when given, else from its content height; it is kept for the life of that open box, so
//   content changes only move `top`/`maxHeight` on that side and the box cannot flip-flop.
//   It is re-chosen only if a scroll/resize leaves that side below MIN_USABLE_ROOM.
// - Updates are synchronous (the effect, scroll/resize handlers, and a ResizeObserver on the box,
//   via flushSync), so there is never a frame with a stale placement.
// - When `onAnchorHidden` is given, it is called (instead of placing) once the anchor has scrolled
//   out of the visible area of a scrolling ancestor.
// - Loop-safe: the box is measured by its natural content height (scrollHeight), which the
//   max-height we apply cannot change, and state is only set when the placement actually changes.
export const useAnchoredPosition = (open, anchorRef, popoverRef, { capRatio = null, onAnchorHidden = null } = {}) => {
  const [placement, setPlacement] = useState(null)
  const applied = useRef(null)
  const side = useRef(null)
  const hiddenCallback = useRef(onAnchorHidden)
  hiddenCallback.current = onAnchorHidden

  useLayoutEffect(() => {
    if (!open) {
      applied.current = null
      side.current = null
      setPlacement(null)
      return undefined
    }
    const update = synchronous => {
      const anchorElement = anchorRef.current
      const popover = popoverRef.current
      if (!anchorElement || !popover) return
      const anchor = anchorElement.getBoundingClientRect()
      // Anchor scrolled out of its scrolling container: report it (the owner closes the box) rather
      // than chase it to somewhere off-screen.
      if (hiddenCallback.current && scrolledOutOfView(anchorElement, anchor)) {
        hiddenCallback.current()
        return
      }
      const viewport = { width: window.innerWidth, height: window.innerHeight }
      const size = { width: popover.offsetWidth, height: popover.scrollHeight + (popover.offsetHeight - popover.clientHeight) }
      if (!side.current || roomOn(side.current, anchor, viewport) < MIN_USABLE_ROOM) {
        side.current = chooseSide(anchor, capRatio ? capRatio * viewport.height : size.height, viewport)
      }
      const next = placeOnSide(side.current, anchor, size, viewport)
      if (samePlacement(applied.current, next)) return
      applied.current = next
      if (synchronous) flushSync(() => setPlacement(next))
      else setPlacement(next)
    }
    const onChange = () => update(true)
    update(false)
    window.addEventListener('scroll', onChange, true)
    window.addEventListener('resize', onChange)
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(onChange)
    if (observer && popoverRef.current) observer.observe(popoverRef.current)
    return () => {
      if (observer) observer.disconnect()
      window.removeEventListener('scroll', onChange, true)
      window.removeEventListener('resize', onChange)
    }
  }, [open, anchorRef, popoverRef, capRatio])

  return placement
}
